import { apiRequest } from '../client';
import type { WalletSummary } from '../../types/admin-dashboard';

export type PaymentType = 'CASH' | 'CREDIT';
export type PaymentOperation = 'DEPOSIT' | 'WITHDRAW';
export type PaymentHistoryFilter = 'ALL' | 'CREDIT' | 'CASH' | 'DRAW_ACCOUNT' | 'MANUAL_ACCOUNT' | 'DIRECT_ACCOUNT';

export type PaymentTransferInput = {
  paymentType: PaymentType;
  operation: PaymentOperation;
  amount: string;
  description?: string;
  idempotencyKey: string;
};

export type PaymentHistoryQuery = {
  type?: PaymentHistoryFilter;
  start?: string;
  end?: string;
  search?: string;
  page?: number;
  pageSize?: number;
};

export type PaymentHistoryRow = {
  id: string;
  createdAt: string;
  paymentType: PaymentType;
  operation: PaymentOperation;
  amount: string;
  upline: string;
  balance: string;
  description: string;
  outgoing: boolean;
};

export type PaymentHistoryResponse = {
  ok: true;
  target: {
    id: string;
    username: string;
    displayName: string;
    role: string;
    wallet: WalletSummary | null;
  };
  actorWallet: WalletSummary | null;
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  transactions: PaymentHistoryRow[];
};

const historyQueryString = (query: PaymentHistoryQuery = {}, includePagination = true) => {
  const params = new URLSearchParams();
  if (query.type && query.type !== 'ALL') params.set('type', query.type);
  if (query.start) params.set('start', query.start);
  if (query.end) params.set('end', query.end);
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (includePagination && query.page) params.set('page', String(query.page));
  if (includePagination && query.pageSize) params.set('pageSize', String(query.pageSize));
  return params.toString();
};

export const adminPaymentsApi = {
  transfer: (accountId: string, input: PaymentTransferInput) => apiRequest<{ ok: true; created: boolean; transactionId: string }>(`/api/wallets/${encodeURIComponent(accountId)}/transfer`, {
    method: 'POST',
    body: JSON.stringify(input),
  }),
  history: (accountId: string, query: PaymentHistoryQuery = {}) => {
    const suffix = historyQueryString(query);
    return apiRequest<PaymentHistoryResponse>(`/api/wallets/${encodeURIComponent(accountId)}/history${suffix ? `?${suffix}` : ''}`);
  },
  historyPdfUrl: (accountId: string, query: PaymentHistoryQuery = {}) => {
    const suffix = historyQueryString(query, false);
    return `/api/wallets/${encodeURIComponent(accountId)}/history/pdf${suffix ? `?${suffix}` : ''}`;
  },
};
